import React, { useMemo } from "react";
import { useFormContext, useFieldArray, useWatch } from "react-hook-form";
import { Plus, Info, Trash2 } from "lucide-react";

import { ButtonShadcn as Button } from "@/components/ui/Button";
import ClearButton from "@/components/ui/ClearButton";
import RHFSelect from "@/components/form/RHFSelect";
import { languageOptions as isoLanguageOptions } from "@/utils/languageOptions";
import RequiredMark from "@/components/ui/RequiredMark";
import { PROFICIENCY_LEVELS, RECRUITER_PROFICIENCY_LEVELS } from "@/config/dropdownData";
import { levelledLanguageKeys } from "@/utils/languageRequirements";
import { cn } from "@/lib/utils";

// Both row-level actions (the per-field clear and the row's own remove) are
// icon-only ghost buttons of the same shape, so a hover reads as one language
// across the row: a plain glyph until you hover it, then a round highlight.
const ICON_BUTTON_CLASS = "h-6 w-6 shrink-0 rounded-full p-0";

// Passed to RHFSelect for its aria-label even though `hideLabel` keeps it from
// drawing its own — declared once so the visible label and the accessible
// name can never drift apart.
const LANGUAGE_LABEL = "Language";
const PROFICIENCY_LABEL = "Proficiency";

/**
 * One requirement per row: a language and the proficiency it needs.
 *
 * **Both halves are closed lists.** A language is a NAME FROM A FIXED
 * CATALOGUE — the ISO 639-1 list that ships with the client
 * (`utils/languageOptions`), so it is complete on first paint and works on the
 * public portal, which has no token to send. Proficiency is a NAME FROM A
 * FIXED CATALOGUE too — `PROFICIENCY_LEVELS`, plus `RECRUITER_UNKNOWN_PROFICIENCY`
 * where `allowUnknownProficiency` is set — so neither select is creatable and a
 * typed term never offers `Add "<term>"`.
 *
 * A stored language the catalogue does not have — an older client wrote it, or
 * it predates this rule — is offered on ITS OWN ROW and nowhere else, so
 * clearing it stays reversible without opening a way to invent a new one.
 *
 * Language and proficiency fill in independently, in either order — a row may
 * keep its proficiency EMPTY (a job may demand a language without naming a
 * level, and the backend stores it as null, matching keys off presence in that
 * case), but a proficiency with no language is refused at submit time
 * (`languagePairRowsSchema`), same as it always was.
 *
 * **Two levels of clear, and one delete.** A `ClearButton` above each select
 * blanks that ONE value, icon-only so it does not compete with the field's own
 * label; the trash icon above the row removes the row outright. Both clears
 * are always on screen and grey out when there is nothing to clear — one that
 * appeared only once its field was filled was invisible exactly when someone
 * was hunting for it, and made the row change shape as they typed. Each row is
 * a thin border-bottom divider, not a bordered card with its own heading — a
 * row of two selects is already visually one unit.
 *
 * Neither picker takes options: proficiency offers exactly `PROFICIENCY_LEVELS`,
 * and language the ISO catalogue minus the languages other rows already hold.
 *
 * @param {string} name      - RHF field-array path holding the rows.
 * @param {boolean} disabled - while the surrounding form submits.
 * @param {string} emptyText - shown when there are no rows yet.
 * @param {Array}  [lockedLanguages]    - language names this record must carry.
 *      A row naming one of them renders its language read-only and cannot be
 *      removed: the candidate answers the job's question with a level, they do
 *      not get to decide whether they are asked it. Matched case-insensitively.
 *      `utils/languageRequirements.requiredLanguageNames` builds this list.
 *      Locking a row and demanding a level are two different facts: EVERY job
 *      language is locked, but only the ones the job itself named a level for
 *      have that level refused blank (`levelledLanguageKeys`). A job asking for
 *      "English, any level" locks the row and accepts it empty.
 * @param {boolean} [languageRequired]    - draw the red marker on every row's
 *      Language label. The candidate flows pass the job's own
 *      `languages.language` flag; the admin profile editors pass nothing,
 *      because no job decides what a profile must carry.
 * @param {Array}  [levelledLanguages]    - the job's own rows. Only a row whose
 *      language the job named WITH a level gets the Proficiency marker — the
 *      one row the schema refuses blank (`levelledLanguageKeys`). A job
 *      levelling German and not English marks German alone, even though its
 *      `languages.proficiency` key is on. Reading the same helper the schema
 *      reads is what keeps a refused field from carrying no marker.
 * @param {boolean} [allowUnknownProficiency] - add `RECRUITER_UNKNOWN_PROFICIENCY`
 *      to the Proficiency list. Passed only by the four screens where a
 *      recruiter enters a CANDIDATE's data on their behalf (bulk upload,
 *      reupload, edit profile, admin apply-to-job) — never where the
 *      candidate fills the row themselves (apply, confirm-details), and never
 *      on the job form, where the picker names what the role needs.
 */
const LanguageProficiencyRows = ({
  name = "languagesRequired",
  disabled = false,
  emptyText,
  lockedLanguages,
  languageRequired = false,
  levelledLanguages,
  allowUnknownProficiency = false,
}) => {
  const { control, setValue } = useFormContext();

  const { fields: languageRows, append: appendLanguageRow, remove: removeLanguageRow } = useFieldArray({ control, name });

  // Per-row values, so each row knows whether it is one of the job's, whether
  // its stored language needs offering, and which of its clears are live.
  const watchedRows = useWatch({ control, name });

  const toSelectOptions = (options) =>
    (options ?? [])
      .map((option) =>
        typeof option === "string"
          ? { value: option, label: option }
          : option?.value != null
            ? option
            : { value: option.name, label: option.name },
      )
      .filter((option) => option.value);

  // Closed catalogue: the predefined levels, on every caller (job form,
  // candidate apply, profile editors) — plus one more, "Unknown", on the four
  // screens where a recruiter fills the row for the candidate. Legacy custom
  // values already stored still render via `fallbackLabel` below and still
  // validate — they just can't be picked anew.
  const proficiencyChoices = useMemo(
    () => toSelectOptions(allowUnknownProficiency ? RECRUITER_PROFICIENCY_LEVELS : PROFICIENCY_LEVELS),
    [allowUnknownProficiency],
  );

  // A language already on the record that the catalogue does not carry. Offered
  // first, on that row alone, so the value survives a clear and can be picked
  // back — the alternative is an edit that silently destroys a name the
  // recruiter now has no way to type.
  //
  // Read from `item` (the field array's own snapshot, which holds what the row
  // ARRIVED with and does not follow edits) as well as the live value. Reading
  // only the live one made clearing irreversible: blanking the field removed
  // the very option needed to put it back.
  //
  // A language another row already holds is not offered: one language, one
  // row. The schema refuses the duplicate at submit; this keeps it from being
  // picked at all.
  const languageChoicesFor = (item, row, index) => {
    const extras = [];
    const seen = new Set(isoLanguageOptions.map((option) => String(option.value).toLowerCase()));
    [item?.language, row?.language].forEach((candidate) => {
      const value = String(candidate ?? "").trim();
      if (!value || seen.has(value.toLowerCase())) return;
      seen.add(value.toLowerCase());
      extras.push({ value, label: value });
    });
    const taken = new Set(
      (watchedRows ?? [])
        .filter((_, other) => other !== index)
        .map((other) => String(other?.language ?? "").trim().toLowerCase())
        .filter(Boolean),
    );
    return [...extras, ...isoLanguageOptions].filter((option) => !taken.has(String(option.value).toLowerCase()));
  };

  const lockedNames = useMemo(
    () => new Set((lockedLanguages ?? []).map((entry) => String(entry?.language ?? entry ?? "").trim().toLowerCase()).filter(Boolean)),
    [lockedLanguages],
  );
  // Keyed on what the row ARRIVED with (`item`), not its live value: the lock
  // belongs to the rows the job prefilled. A row the candidate added arrives
  // blank, so it stays theirs to clear and remove whatever it ends up naming.
  const isLocked = (item) => lockedNames.has(String(item?.language ?? "").trim().toLowerCase());

  // A level is demanded on one row alone: the one naming a language the job
  // gave a level. `languagePairRowsStrictSchema` refuses on exactly that
  // ground, and the marker reads the same helper it does, so a field can never
  // be refused without carrying one.
  const levelledNames = useMemo(() => levelledLanguageKeys(levelledLanguages), [levelledLanguages]);
  const needsLevel = (row) => levelledNames.has(String(row?.language ?? "").trim().toLowerCase());

  return (
    <div className="space-y-2" data-field={name}>
      {languageRows.length === 0 && emptyText && <p className="text-[13px] text-text-secondary">{emptyText}</p>}
      {/* Both controls are closed lists — nothing here is free-typed. */}
      <p className="text-[12px] text-text-secondary flex items-center gap-1.5">
        <Info size={12} className="shrink-0" />
        Pick a language from the list, then select the proficiency you need.
      </p>
      {languageRows.map((item, index) => {
        const row = watchedRows?.[index] || {};
        const locked = isLocked(item);
        const levelRequired = needsLevel(row);
        const hasLanguage = Boolean(row.language);
        const hasProficiency = Boolean(row.proficiency);
        const clearField = (field) => setValue(`${name}.${index}.${field}`, "", { shouldValidate: true });
        return (
          <div key={item.id} className="pb-2 border-b border-border-primary/60 last:border-0 last:pb-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 items-start">
              <div>
                <label className="block text-sm mb-1.5 font-medium text-text-secondary" htmlFor={`${name}.${index}.language`}>
                  {LANGUAGE_LABEL}
                  {languageRequired && <RequiredMark />}
                </label>
                {/* The clear control sits beside the field it clears, not
                    above it, so it reads as part of the same control. */}
                <div className="flex items-center gap-1.5">
                  <div className="min-w-0 flex-1">
                    <RHFSelect
                      hideLabel
                      disabled={disabled}
                      // `readOnly`, never `disabled`: a locked language is the
                      // job's own requirement and MUST ride on the payload.
                      // react-hook-form omits a disabled Controller's value
                      // from the submission, so routing the lock through
                      // `disabled` sent `language: undefined` for every
                      // job-required row and the review schema refused it with
                      // "expected string".
                      readOnly={locked}
                      name={`${name}.${index}.language`}
                      label={LANGUAGE_LABEL}
                      placeholder="Search for a language"
                      options={languageChoicesFor(item, row, index)}
                      searchable
                      searchPlaceholder="Search languages..."
                      fallbackLabel={row.language || undefined}
                    />
                  </div>
                  {/* Locked (job-prefilled) rows offer no language clear at
                      all — not even greyed — while a row the candidate added
                      keeps its clear whenever it holds a language. */}
                  {!locked && (
                    <ClearButton
                      label={`Clear language for row ${index + 1}`}
                      canClear={hasLanguage}
                      onClick={() => clearField("language")}
                      disabled={disabled}
                      className={ICON_BUTTON_CLASS}
                    >
                      {null}
                    </ClearButton>
                  )}
                </div>
                {locked && <p className="mt-1 text-[11px] text-text-secondary">Required by this job</p>}
              </div>
              <div>
                {/* The row's own Remove rides the Proficiency label's line
                    instead of a row of its own — a row the job requires has
                    no Remove at all, it is the question, not one of the
                    candidate's answers. */}
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-text-secondary" htmlFor={`${name}.${index}.proficiency`}>
                    {PROFICIENCY_LABEL}
                    {levelRequired && <RequiredMark />}
                  </label>
                  {!locked && (
                    <Button
                      variant="ghost"
                      size="xs"
                      aria-label={`Remove language row ${index + 1}`}
                      title={`Remove language row ${index + 1}`}
                      onClick={() => removeLanguageRow(index)}
                      disabled={disabled}
                      className={cn(ICON_BUTTON_CLASS, "text-text-secondary hover:text-red-400")}
                    >
                      <Trash2 aria-hidden="true" />
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="min-w-0 flex-1">
                    <RHFSelect
                      hideLabel
                      // A level with no language is a requirement about
                      // nothing, and `languagePairRowsSchema` refuses it at
                      // submit. Shutting the field until the language is
                      // named is that same rule said earlier, so nobody fills
                      // in a level that was never going to be accepted. A
                      // locked row is exempt: its language IS the job's, it is
                      // already answered, and the level is the whole question.
                      disabled={disabled || (!hasLanguage && !locked)}
                      name={`${name}.${index}.proficiency`}
                      label={PROFICIENCY_LABEL}
                      placeholder="Select a level"
                      options={proficiencyChoices}
                      searchable
                      searchPlaceholder="Search levels..."
                      fallbackLabel={row.proficiency || undefined}
                    />
                  </div>
                  {/* Locked (job-prefilled) rows offer no proficiency clear
                      either — same as the language clear above. A row the
                      candidate added keeps its clear whenever it holds a
                      level, so manual rows stay fully editable. */}
                  {!locked && (
                    <ClearButton
                      label={`Clear proficiency for row ${index + 1}`}
                      canClear={hasProficiency}
                      onClick={() => clearField("proficiency")}
                      disabled={disabled}
                      className={ICON_BUTTON_CLASS}
                    >
                      {null}
                    </ClearButton>
                  )}
                </div>
                {!hasLanguage && !locked && <p className="mt-1 text-[11px] text-text-secondary">Add a language first.</p>}
              </div>
            </div>
          </div>
        );
      })}
      {/* `secondary` IS `bg-button-secondary text-text-base` — the classes this
          button used to repeat by hand, before it came from the kit. */}
      <Button
        variant="secondary"
        size="lg"
        onClick={() => appendLanguageRow({ language: "", proficiency: "" })}
        disabled={disabled}
        className="mt-2"
      >
        <Plus aria-hidden="true" /> Add Language
      </Button>
    </div>
  );
};

export default LanguageProficiencyRows;
